import { Stack, Typography, Button, Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AnalysisCard } from './components/analysisCard';
import { ProductCard } from './components/productCard';

import { Swiper, SwiperSlide } from 'swiper/react';

import DynamicProgressBar from '../../components/progressBars/dynamicProgressBar';


import { AnimationLoadingScreenWithProgressBar } from '../../components/loading/loadingScreen';

import { getThemeHexColor } from '../../utils/miscellaneous';

import APIObjectsContext from '../../context/APIObjectsContext';
import MiscContext from '../../context/MiscContext';
import { useNuralogixApi } from '../../api/nuralogix';
import { useAssessmentApi } from '../../api/assessment';
import { useQuestionnaireApi } from '../../api/demographics';

import { useProductsApi } from '../../api/products'


// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';

import './styles.css';


// import required modules
import { Pagination } from 'swiper/modules';

import React, { useContext, useState, useEffect } from 'react';

/**
 * Component for user login.
 *
 * @returns {JSX.Element} - The rendered LoginPage component.
 */


export const HeartHealthAnalysisPage = () => {

    const { userAssessmentID, setUserAssessmentID } = useContext(APIObjectsContext);
    const { userPlan } = useContext(MiscContext);

    // apis
    const nuralogixApi = useNuralogixApi();
    const assessmentApi = useAssessmentApi();
    const productsApi = useProductsApi();

    const [symptomCheckerResults, setSymptomCheckerResults] = useState([]);



    const [overallHeartHealthScore, setOverallHeartHealthScore] = useState([]);

    const [heartResultScore, setHeartResultScore] = useState({});

    const [productsList, setProductsList] = useState([]);



    const [isVitalSignsRecord, setIsVitalSignsRecord] = useState(null);


    const [responseReturned, setResponseReturned] = useState(false);
    const [isSymptomCheckerPresent, setIsSymptomCheckerPresent] = useState(false);
    const [isLoading, setIsLoading] = useState({
        loadingNuralogix: true,
        loadingSymptomChecker: true,
    });

    const [apiResults, setApiResults] = useState({
        isFaceScan: false,
        isSymptomChecker: false,
    });

    const questionnaireApi = useQuestionnaireApi();

    const [demographicsData, setDemographicsData] = useState({});


    useEffect(() => {

        const fetchData = async () => {
            //get user demographics to extract age
            const demographics = await questionnaireApi.getQuestionnaire();

            if (demographics.status === 200) setDemographicsData(demographics.data);

            //get user assessment id
            const assessment = await assessmentApi.getLatestAssessment();
            if (assessment.status === 200) {
                setUserAssessmentID(assessment.data.id);
                if (assessment.data.symptoms_description !== null) {
                    setIsSymptomCheckerPresent(true);
                    const assessmentResults = await assessmentApi.getResultsAssessment(assessment.data.id);
                    if (assessmentResults.status === 200) {
                        const symptomsList = assessmentResults.data.conditions.map((condition, index) => {
                            return {
                                id: index,
                                name: condition.name,
                                probability: condition.probability,
                                symptomColour: getThemeHexColor(),
                            };
                        });

                        // Sort the symptomsList array based on probability in descending order
                        symptomsList.sort((a, b) => b.probability - a.probability);

                        // Get the top 3 items from the sorted array
                        const top3Symptoms = symptomsList.slice(0, 3);

                        setSymptomCheckerResults(top3Symptoms);
                        setApiResults(prev => {
                            return {
                                ...prev,
                                isSymptomChecker: true,
                            };
                        });
                    } else {
                        setIsSymptomCheckerPresent(false);
                    }
                }
            }

            setResponseReturned(true);
            setIsLoading({
                loadingNuralogix: false,
                loadingSymptomChecker: false,
            });
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (userAssessmentID == undefined) return;

            let resultsVitals;
            try {
                resultsVitals = await nuralogixApi.getNuralogixResultsVitals(userAssessmentID);
                setApiResults(prev => {
                    return {
                        ...prev,
                        isFaceScan: true,
                    };
                });
            } catch {
                setResponseReturned(true);
                setIsLoading({
                    loadingNuralogix: false,
                    loadingSymptomChecker: isSymptomCheckerPresent,
                });
                return;
            }

            const res = resultsVitals;
            setIsVitalSignsRecord(Object.keys(res.data).length > 0 ? true : false);

            const heartHealthScore = [
                {
                    key: 'pulserate',
                    title: 'Pulse Rate',
                    vitalStatus: 'Optimal',
                    vitalSignInitials: 'beats/min',
                    resultVal: res.data.pulse_rate,
                    irregularHbeat: res.data.irregular_heartbeat_count,
                    barsVal: [0, 40, 60, 100],
                    details: 'Pulse is the number of times your heart beats in one minute. This shows how hard your heart is working to pump blood around your body. Low values are usually good for resting adults, indicating a healthy heart, but too low might signal a problem whereas high values may indicate stress or heart issues.'
                },
                {
                    key: 'breathingrate',
                    title: 'Breathing Rate',
                    vitalStatus: 'Optimal',
                    vitalSignInitials: 'beats/min',
                    resultVal: res.data.respiration,
                    irregularHbeat: res.data.irregular_heartbeat_count,
                    barsVal: [0, 12, 25, 35],
                    details: 'Breathing rate is the number of times you breathe in and out in a minute. This tells you if your lungs are providing enough oxygen to your body without too much effort. Both very high and very low rates can be bad, indicating potential breathing or health issues.'
                },
                {
                    key: 'systolic_blood_pressure',
                    title: 'Systolic Blood Pressure',
                    vitalStatus: 'Optimal',
                    vitalSignInitials: 'bp',
                    resultVal: res.data.blood_pressure_systolic,
                    irregularHbeat: res.data.irregular_heartbeat_count,
                    barsVal: [0, 90, 120, 129, 130, 139],
                    details: 'Systolic blood pressure is the pressure in your arteries when your heart beats and pushes out blood. High values are bad, indicating potential hypertension; low values might be good but can be concerning if too low.'
                },
                {
                    key: 'diastolic_blood_pressure',
                    title: 'Diastolic Blood Pressure',
                    vitalStatus: 'Optimal',
                    vitalSignInitials: 'bp',
                    resultVal: res.data.blood_pressure_diastolic,
                    irregularHbeat: res.data.irregular_heartbeat_count,
                    barsVal: [0, 60, 80, 84, 85, 89],
                    details: 'Diastolic blood pressure is the pressure in your arteries when your heart is resting between beats. Low numbers could mean your heart is resting well between beats, which is good, but too low might indicate not enough pressure to push blood to all parts of your body, which is not good.'
                }
            ];

            console.log('overhallheartscore', res.data.heart_disease_risk);

            setOverallHeartHealthScore(heartHealthScore);
            setHeartResultScore({heartHealthVal: res.data.heart_disease_risk});

            setResponseReturned(true);
            setIsLoading({
                loadingNuralogix: false,
                loadingSymptomChecker: isSymptomCheckerPresent,
            });
        };



        fetchData();
    }, [userAssessmentID, demographicsData]);

    const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const isGalaxyFold = useMediaQuery('(max-width:280px)');

// Get Producrts Records

useEffect(() => {

    const fetchData = async () => {

        if (userAssessmentID == undefined) return;

        let resultsProducts;

        try {
            resultsProducts = await productsApi.getRecommendedProducts(userAssessmentID);
            setApiResults(prev => {
                return {
                    ...prev,
                    isFaceScan: true,
                };
            });
        } catch {
            setResponseReturned(true);
            setIsLoading({
                loadingNuralogix: false,
                loadingSymptomChecker: isSymptomCheckerPresent,
            });
            return;
        }

        setProductsList(resultsProducts);

        setResponseReturned(true);
        setIsLoading({
            loadingNuralogix: false,
            loadingSymptomChecker: isSymptomCheckerPresent,
        });
    };

    fetchData();

}, [userAssessmentID]);



    // setOverallAnalysisData(analysisData);

    console.log('health Score', overallHeartHealthScore)

    return (


        <>

            {isLoading.loadingNuralogix && isLoading.loadingSymptomChecker ? (
                <AnimationLoadingScreenWithProgressBar
                    title="Loading..."
                    secondsToLoad={60}
                    isAlmostComplete={responseReturned}
                />
            )
                : (


                    <Stack display='flex' flexDirection='row' alignItems="center" justifyContent="center" className='analysis-page-wrapper' paddingTop={5} paddingBottom={1} >
                        <Stack className='analysis-page-card' padding={3}>

                            {/* Top Header Starts */}
                            <Stack className='top-header' display="flex" alignItems='center' flexDirection="row" justifyContent='space-between'>
                                <Stack>
                                    <Typography
                                        color="black"
                                        variant="h4" component="h4" fontWeight="bold"
                                        sx={{
                                            fontSize: isGalaxyFold ? '18px' : isSmallScreen ? '23px' : '23px',
                                        }}
                                    >
                                        Heart Health Score
                                    </Typography>
                                    <Typography
                                        color="#000"
                                        variant="p" component="p" fontWeight="600"
                                        sx={{
                                            fontSize: isGalaxyFold ? '15px' : isSmallScreen ? '15px' : '15px',
                                        }}
                                    >
                                        Top 13% of women.
                                    </Typography>
                                    <Typography
                                        color="#50BA47"
                                        variant="p" component="p" fontWeight="600"
                                        sx={{
                                            fontSize: isGalaxyFold ? '15px' : isSmallScreen ? '15px' : '15px',
                                        }}
                                    >
                                        Very good
                                    </Typography>
                                </Stack>
                                <div
                                    style={{
                                        position: 'relative',
                                        width: '80px',
                                        height: '80px',
                                        background: '#fff', // Add a contrasting background color
                                    }}
                                >
                                    <Box position="relative" display="inline-flex">
                                        <DynamicProgressBar paramValue={Number(Math.round(heartResultScore.heartHealthVal).toFixed(0))} paramSize={80} paramThickness={8} />

                                        <Typography
                                            variant="h6"
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                            }}
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {Number(Math.round(heartResultScore.heartHealthVal).toFixed(0))}
                                        </Typography>

                                    </Box>

                                </div>

                            </Stack>
                            {/* Top Header Ends */}

                            {/* TXT Box Starts */}

                            <Box>
                                <Typography
                                    color="black"
                                    variant="h4" component="h4" fontWeight="bold"
                                    sx={{
                                        fontSize: isGalaxyFold ? '18px' : isSmallScreen ? '23px' : '23px',
                                    }}
                                >
                                    👍 Looks Great!
                                </Typography>
                                <Typography
                                    marginTop='7px'
                                    color="black"
                                    variant="p" component="p" fontWeight="bold"
                                    sx={{
                                        fontWeight: '500',
                                        fontSize: isGalaxyFold ? '14px' : isSmallScreen ? '14px' : '14px',
                                    }}
                                >
                                    The following health scores are looking great! Keep doing what you’re doing.
                                </Typography>
                            </Box>
                            {/* TXT Box Ends */}


                            <Stack className='analysis-slider'>

                                <Swiper

                                    slidesPerView={'auto'}
                                    spaceBetween={30}
                                    pagination={{
                                        clickable: true,
                                    }}
                                    modules={[Pagination]}
                                    className="mySwiper"

                                >
                                    {overallHeartHealthScore.map((data) => (
                                        <SwiperSlide key={data.key}>
                                            <AnalysisCard
                                                keyName={data.key}
                                                title={data.title}
                                                vitalStatus={data.vitalStatus}
                                                vitalSignInitials={data.vitalSignInitials}
                                                resultVal={data.resultVal}
                                                irregularHbeat={data.irregularHbeat}
                                                barsVal={data.barsVal}
                                                details={data.details}
                                            />
                                        </SwiperSlide>
                                    ))}

                                </Swiper>

                            </Stack>

                            {/* Product Wrapper Starts */}
                            <Stack>

                                {/* Product Header Starts */}
                                <Box marginTop='30px'>
                                    <Typography
                                        color="black"
                                        variant="h4" component="h4" fontWeight="bold"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: isGalaxyFold ? '23px' : isSmallScreen ? '23px' : '23px',
                                        }}
                                    >
                                        💊 Products to Improve Your Heart Health
                                    </Typography>
                                    <Typography
                                        color="black"
                                        variant="h4" component="h4" fontWeight="bold"
                                        sx={{
                                            fontWeight: 500, marginTop: '5px',
                                            fontSize: isGalaxyFold ? '14px' : isSmallScreen ? '14px' : '14px',
                                        }}
                                    >
                                        Our recommendations are informed by real results and positive impacts observed in individuals who share similar health profiles and lifestyle factors as yours.
                                    </Typography>
                                </Box>
                                {/* Product Header Starts */}

                                {/* Product SLider Starts */}
                                <Stack className='product-slider'>

                                    <Swiper

                                        slidesPerView={'auto'}
                                        spaceBetween={30}
                                        pagination={{
                                            clickable: true,
                                        }}
                                        modules={[Pagination]}
                                        className="productSwiper"

                                    >
                                        {productsList.map((product) => (
                                            <SwiperSlide key={product.id}>
                                                <ProductCard productData={product} />
                                            </SwiperSlide>
                                        ))}

                                    </Swiper>

                                </Stack>
                                {/* Product SLider Ends */}

                                {/* Discord Card Starts */}
                                <Stack className='discord-card' maxWidth='350px' minHeight='85px' marginTop='1.88rem' display='flex' flexDirection='row' justifyContent='space-evenly' alignItems='center'>
                                    <Box>
                                        <img src="/socialicons/discord.svg" alt="" />
                                    </Box>
                                    <Box>
                                        <Typography
                                            color="black"
                                            variant="h4" component="h4" fontWeight="bold"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: isGalaxyFold ? '17px' : isSmallScreen ? '17px' : '17px',
                                            }}
                                        >
                                            Join our AI-MD community!
                                        </Typography>
                                        <Button
                                            sx={{
                                                marginTop: '6px',
                                                width: '240px',
                                                height: '28px',
                                                borderRadius: '30px',
                                                background: '#4F5AE7'
                                            }}
                                        >
                                            <Typography
                                                color="#FFF"
                                                variant="p" component="p" fontWeight="bold"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: isGalaxyFold ? '14px' : isSmallScreen ? '14px' : '14px',
                                                }}
                                            >
                                                Join our AI-MD community!
                                            </Typography>
                                        </Button>
                                    </Box>
                                </Stack>
                                {/* Discord Card Ends */}

                                {/* Bottom Stuff Starts */}
                                <Box marginTop='10px'>
                                    <Typography
                                        color="black"
                                        variant="h4" component="h4" fontWeight="bold"
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: isGalaxyFold ? '10px' : isSmallScreen ? '10px' : '10px',
                                        }}
                                    >
                                        These numbers are only estimates and are not a substitute for the judgment of a healthcare professional. They are intended to improve awareness and general wellness. Please see our How it Works page.
                                    </Typography>
                                </Box>
                                {/* Bottom Stuff Ends */}

                            </Stack>
                            {/* Product Wrapper Ends */}

                        </Stack>
                    </Stack >

                )}


        </>

    );
};
